import {
  COUNT_REASONS,
  COUNT_REASON_LABELS,
  COUNT_RESOLUTION_LABELS,
  barcodeCandidates,
  normalizeScan,
  scanSpellings,
  type CountApproval,
  type CountOrderResult,
  type CountReview,
  type CountSheet,
  type CountSheetLine,
  type CountSubmitResult,
  type CountTaskSummary,
} from '@wms/domain';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError, errorMessage } from '../api';
import { draftTaskIds, dropDraft, fromSheet, hasCounts, readDraft, restoreLines, writeDraft, type FormLine } from '../counting';
import { feedback } from '../feedback';
import { binCode, fmt } from '../format';
import { guardLeave, leave } from '../router';
import { useSession } from '../session';
import { Empty, ErrorBanner, Modal, ProductThumb, ScanField, SellerChip, Spinner, useLoad, useToast } from '../ui';
import { Icon } from '../ui/icons';
import { useSort } from '../ui/sort';

type Tab = 'count' | 'review' | 'history';

interface Message {
  readonly tone: 'ok' | 'bad' | 'warn' | 'info';
  readonly text: string;
}

/**
 * Göz sayımı: bins locked after a short pick and the supervisor's count orders. The counter
 * scans the bin and counts blind — the screen never shows how many the system expects. A count
 * that matches unlocks the bin; a difference goes to the supervisor's approval tab.
 */
export function Counts({ onChange }: { onChange: () => void }) {
  const { warehouseId, isSupervisor, me } = useSession();
  const [tab, setTab] = useState<Tab>('count');
  const tasks = useLoad(
    () => (warehouseId ? api<CountTaskSummary[]>('GET', `/api/wms/counts?warehouseId=${warehouseId}`) : Promise.resolve([])),
    [warehouseId],
    15_000,
  );
  const [sheet, setSheet] = useState<{ sheet: CountSheet; scannedBin: string | null } | null>(null);
  const [message, setMessage] = useState<Message | null>(null);
  const [ordering, setOrdering] = useState(false);
  // counts entered in the open bin: asked before they are left (Vazgeç, another bin, another screen)
  const [dirty, setDirty] = useState(false);
  const [asking, setAsking] = useState<{ then: () => void } | null>(null);
  // the count tab is hidden behind the others, not closed: back on it, its scan field takes the cursor again
  const [visits, setVisits] = useState(0);
  const showCount = () => {
    if (tab !== 'count') setVisits((v) => v + 1);
    setTab('count');
  };
  const toast = useToast();

  const all = tasks.data ?? [];
  const open = all.filter((t) => t.status === 'open');
  const review = all.filter((t) => t.status === 'review');
  const drafted = draftTaskIds(me.id, open);

  const holding = sheet !== null && dirty;
  const away = (then: () => void) => (holding ? setAsking({ then }) : then());
  useEffect(() => {
    if (!holding) return;
    return guardLeave((to) => {
      setAsking({ then: () => leave(to) });
      return true;
    });
  }, [holding]);

  const changed = () => {
    void tasks.reload();
    onChange();
  };

  // the counter scans a bin label: its sheet opens, the bin already confirmed
  const openByScan = async (code: string) => {
    if (!warehouseId) return;
    try {
      const found = await api<CountSheet>('GET', `/api/wms/counts/by-location?warehouseId=${warehouseId}&barcode=${encodeURIComponent(code)}`);
      feedback('ok');
      setMessage(null);
      setSheet({ sheet: found, scannedBin: code });
    } catch (e) {
      feedback('error');
      setMessage({ tone: 'bad', text: errorMessage(e) });
    }
  };

  const openFromList = async (task: CountTaskSummary) => {
    try {
      setSheet({ sheet: await api<CountSheet>('GET', `/api/wms/counts/${task.id}/sheet`), scannedBin: null });
      setMessage(null);
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };
  const switchTo = (task: CountTaskSummary) => {
    if (sheet?.sheet.task.id === task.id) return;
    away(() => {
      setSheet(null);
      void openFromList(task);
    });
  };

  if (tasks.error) return <ErrorBanner message={tasks.error} onRetry={() => void tasks.reload()} />;
  if (!tasks.data) return <Spinner />;

  return (
    <div className="stack counts">
      <div className="row">
        <div className="tabs" role="tablist">
          <button className={tab === 'count' ? 'on' : ''} role="tab" aria-selected={tab === 'count'} onClick={showCount}>
            Sayılacak gözler ({open.length})
          </button>
          {isSupervisor && (
            <button className={tab === 'review' ? 'on' : ''} role="tab" aria-selected={tab === 'review'} onClick={() => setTab('review')}>
              Fark onayı ({review.length})
            </button>
          )}
          <button className={tab === 'history' ? 'on' : ''} role="tab" aria-selected={tab === 'history'} onClick={() => setTab('history')}>
            Geçmiş
          </button>
        </div>
        <span style={{ flex: 1 }} />
        {isSupervisor && (
          <button className="btn" onClick={() => setOrdering(true)}>
            <Icon name="plus" /> Sayım emri ver
          </button>
        )}
      </div>

      {/* hidden behind the other tabs: a bin being counted stays as it is */}
      <div className="split" hidden={tab !== 'count'}>
        <div className="stack">
          {message && <div className={`banner ${message.tone}`}>{message.text}</div>}
          {sheet ? (
            <CountForm
              key={sheet.sheet.task.id}
              sheet={sheet.sheet}
              scannedBin={sheet.scannedBin}
              visits={visits}
              onDirty={setDirty}
              onClose={() => away(() => setSheet(null))}
              onGone={(text) => {
                setSheet(null);
                setMessage({ tone: 'warn', text });
                changed();
              }}
              onDone={(result) => {
                setSheet(null);
                setMessage(
                  result.status === 'closed'
                    ? { tone: 'ok', text: `${result.locationCode}: sayım tuttu, gözün kilidi kaldırıldı.` }
                    : { tone: 'warn', text: `${result.locationCode}: sayım gönderildi. Sorumlu onayından sonra göz açılacak.` },
                );
                changed();
              }}
            />
          ) : (
            <section className="card">
              <div className="card-b stack">
                <ScanField key={visits} placeholder="Sayılacak gözün etiketini okutun" onScan={openByScan} suggest={{ kinds: ['bin'] }} hint="Gözün etiketini okutun ya da gözü listeden seçin." />
              </div>
            </section>
          )}
        </div>
        <section className="card">
          <div className="card-h">
            <h2>Sayılacak gözler</h2>
            <span className="sub">{open.length} göz</span>
          </div>
          {open.length === 0 ? (
            <Empty title="Sayılacak göz yok">Eksik toplanan gözler ve sorumlunun sayım emirleri burada belirir.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="t stack-sm count-rows">
                <tbody>
                  {open.map((t) => (
                    <tr key={t.id} className={`clickable${sheet?.sheet.task.id === t.id ? ' selected' : ''}`} onClick={() => switchTo(t)}>
                      <td className="mono" style={{ fontWeight: 700 }}>
                        {t.locationCode}
                      </td>
                      <td>
                        <span className={`chip ${t.reason === 'short_pick' ? 'bad' : 'info'}`}>{COUNT_REASON_LABELS[t.reason]}</span>
                        {t.round > 1 && <span className="chip warn" style={{ marginLeft: 4 }}>{t.round}. sayım</span>}
                        {drafted.has(t.id) && sheet?.sheet.task.id !== t.id && (
                          <span className="chip info" style={{ marginLeft: 4 }}>
                            Yarım kaldı
                          </span>
                        )}
                        {t.note && (
                          <div className="muted" style={{ fontSize: 12 }}>
                            {t.note}
                          </div>
                        )}
                      </td>
                      <td className="nowrap muted">{fmt.age(t.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!isSupervisor && review.length > 0 && <div className="card-b muted">{review.length} sayım sorumlu onayını bekliyor.</div>}
        </section>
      </div>

      {tab === 'review' && isSupervisor && <ReviewQueue tasks={review} onChange={changed} />}
      {tab === 'history' && <History warehouseId={warehouseId} canReview={isSupervisor} />}
      {asking && sheet && (
        <Modal
          title="Sayılan adetler silinsin mi?"
          onClose={() => setAsking(null)}
          footer={
            <>
              <button className="btn big" onClick={() => setAsking(null)}>
                Saymaya dön
              </button>
              <button
                className="btn big"
                onClick={() => {
                  setAsking(null);
                  asking.then();
                }}
              >
                Hayır, sakla
              </button>
              <button
                className="btn big danger"
                onClick={() => {
                  dropDraft(me.id, sheet.sheet.task.id);
                  setAsking(null);
                  asking.then();
                }}
              >
                Evet, sil
              </button>
            </>
          }
        >
          <p style={{ margin: 0 }}>
            <b>{sheet.sheet.task.locationCode}</b> gözünde saydığınız adetler bu telefonda saklı. Saklarsanız gözü yeniden açtığınızda kaldığınız yerden devam edersiniz.
          </p>
        </Modal>
      )}
      {ordering && warehouseId && (
        <OrderDialog
          warehouseId={warehouseId}
          onClose={() => setOrdering(false)}
          onDone={(result) => {
            setOrdering(false);
            toast(
              `${result.created} göz sayıma alındı${result.skipped > 0 ? `, ${result.skipped} göz zaten sayımdaydı` : ''}`,
              result.created > 0 ? 'ok' : 'info',
            );
            changed();
          }}
        />
      )}
    </div>
  );
}

/** The blind count of one bin: the products to count, a box for each, scans add to it. */
function CountForm(props: {
  sheet: CountSheet;
  scannedBin: string | null;
  /** the count tab shown again: the scan field is new, so it takes the cursor */
  visits: number;
  onDirty: (dirty: boolean) => void;
  onClose: () => void;
  /** the task closed meanwhile (sent from another phone, or the supervisor decided) */
  onGone: (text: string) => void;
  onDone: (result: CountSubmitResult) => void;
}) {
  const { sheet, onDirty } = props;
  const task = sheet.task;
  const { isSupervisor, me } = useSession();
  const [bin, setBin] = useState<string | null>(props.scannedBin);
  // a half-done count of this bin left on this phone comes back
  const [restored] = useState(() => readDraft(me.id, task));
  const [lines, setLines] = useState<FormLine[]>(() => (restored ? restoreLines(sheet.lines.map(fromSheet), restored.lines) : sheet.lines.map(fromSheet)));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [choose, setChoose] = useState<{ candidates: { line: CountSheetLine; units: number }[] } | null>(null);
  const [hit, setHit] = useState<string | null>(null);

  // every change is kept on the phone until the count is sent
  const sent = useRef(false);
  useEffect(() => {
    if (!sent.current) writeDraft(me.id, task, lines);
    onDirty(hasCounts(lines));
  }, [lines]);
  useEffect(() => () => onDirty(false), []);
  // picked from the list below: on a phone the form comes into view
  const card = useRef<HTMLElement>(null);
  useEffect(() => {
    // braces: scrollIntoView returns a promise in newer browsers, which an effect must not return
    card.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  const confirmBin = (code: string) => {
    if (code === task.locationBarcode || code === task.locationCode.toUpperCase()) {
      feedback('ok');
      setBin(code);
      setError(null);
    } else {
      feedback('error');
      setError(`Yanlış göz: ${binCode(code)} okutuldu, ${task.locationCode} sayılıyor.`);
    }
  };

  const bump = (key: string, units: number) => {
    setLines((all) => all.map((l) => (l.key === key ? { ...l, qty: (l.qty ?? 0) + units } : l)));
    setHit(key);
    setTimeout(() => setHit((h) => (h === key ? null : h)), 600);
    feedback('ok');
  };

  const addFound = (candidate: { line: CountSheetLine; units: number }) => {
    const key = `found:${candidate.line.productId}:${Date.now()}`;
    setLines((all) => [...all, { key, line: candidate.line, qty: candidate.units, found: true, lotNo: candidate.line.lotNo ?? '', expiryDate: candidate.line.expiryDate ?? '' }]);
    setHit(key);
    feedback('ok');
  };

  // a product scan adds to its line; a product the sheet did not name is looked up and added
  const onProduct = async (code: string) => {
    const matching = (codes: readonly string[], skus: readonly string[]) =>
      lines.filter((l) => l.line.barcodes.some((b) => codes.includes(b.code)) || skus.includes(l.line.sku.toUpperCase()));
    // the code as read; failing that, as a reader on another keyboard layout meant it (AB.12 ↔ AB/12)
    const exact = barcodeCandidates(code);
    const asRead = matching(exact.codes, [normalizeScan(code)]);
    const { codes, gs1 } = asRead.length > 0 ? exact : barcodeCandidates(code, { layouts: true });
    const matches = asRead.length > 0 ? asRead : matching(codes, scanSpellings(code));
    const units = (l: FormLine) => l.line.barcodes.find((b) => codes.includes(b.code))?.units ?? 1;
    const byLot = gs1?.lot ? matches.filter((l) => l.lotNo === gs1.lot) : matches;
    if (byLot.length === 1) return bump(byLot[0]!.key, units(byLot[0]!));
    if (matches.length > 1) {
      feedback('error');
      return setError(`${matches[0]!.line.sku} gözde birden fazla lotla var: adedi ilgili lot satırına yazın.`);
    }
    try {
      const found = await api<{ line: CountSheetLine; units: number }[]>('GET', `/api/wms/counts/${task.id}/product?barcode=${encodeURIComponent(code)}`);
      if (found.length === 0) {
        feedback('error');
        return setError(`${code}: hiçbir satıcının ürünlerinde tanımlı değil.`);
      }
      setError(null);
      if (found.length === 1) addFound(found[0]!);
      else setChoose({ candidates: found });
    } catch (e) {
      feedback('error');
      setError(errorMessage(e));
    }
  };

  const setQty = (key: string, raw: string) => {
    const value = raw.trim() === '' ? null : Math.max(0, Math.floor(Number(raw)));
    setLines((all) => all.map((l) => (l.key === key ? { ...l, qty: value === null || Number.isNaN(value) ? null : value } : l)));
  };

  const uncounted = lines.filter((l) => l.qty === null).length;
  const ready = bin !== null && uncounted === 0 && lines.every((l) => !l.found || l.qty === 0 || ((!l.line.trackLot || l.lotNo.trim()) && (!l.line.trackExpiry || l.expiryDate)));

  const submit = async () => {
    if (!bin) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api<CountSubmitResult>('POST', `/api/wms/counts/${task.id}/submit`, {
        locationBarcode: bin,
        lines: lines.map((l) => ({ productId: l.line.productId, lotNo: l.lotNo.trim() || null, expiryDate: l.expiryDate || null, countedQty: l.qty ?? 0 })),
      });
      feedback('done');
      sent.current = true;
      dropDraft(me.id, task.id);
      props.onDone(result);
    } catch (e) {
      feedback('error');
      setBusy(false);
      // sent before the connection dropped, or the supervisor closed it: nothing to keep
      if (e instanceof ApiError && e.code === 'COUNT_NOT_OPEN') {
        sent.current = true;
        dropDraft(me.id, task.id);
        return props.onGone(`${task.locationCode}: ${errorMessage(e)}`);
      }
      // products put on the shelf meanwhile: the sheet comes again with them, to be counted too
      if (e instanceof ApiError && e.code === 'COUNT_INCOMPLETE') {
        const fresh = await api<CountSheet>('GET', `/api/wms/counts/${task.id}/sheet`).catch(() => null);
        if (fresh) setLines((all) => [...all, ...fresh.lines.map(fromSheet).filter((n) => !all.some((l) => l.key === n.key))]);
      }
      setError(errorMessage(e));
    }
  };

  return (
    <section className="card count-card" ref={card}>
      <div className="card-b stack">
        <div>
          {/* Vazgeç beside the chips: the bin's code keeps the whole width of a phone */}
          <div className="row">
            <span className={`chip ${task.reason === 'short_pick' ? 'bad' : 'info'}`}>{COUNT_REASON_LABELS[task.reason]}</span>
            {task.round > 1 && <span className="chip warn">{task.round}. sayım</span>}
            <span className="mono muted">{task.taskNumber}</span>
            <button className="btn ghost" style={{ marginLeft: 'auto' }} onClick={props.onClose}>
              <Icon name="x" /> Vazgeç
            </button>
          </div>
          <div className="loc-big" style={{ marginTop: 4 }}>
            {task.locationCode}
          </div>
          {task.note && <div className="muted">{task.note}</div>}
        </div>
        {restored && (
          <div className="banner info">
            <Icon name="undo" />
            <div>
              Yarım kalan sayım geri yüklendi.
              <div style={{ fontWeight: 500 }}>Adetleri kontrol edip kaldığınız yerden devam edin.</div>
            </div>
          </div>
        )}
        {error && <div className="banner bad">{error}</div>}
        {bin === null ? (
          <>
            <ScanField key={`bin-${props.visits}`} placeholder={`Önce ${task.locationCode} gözünün etiketini okutun`} onScan={confirmBin} suggest={{ kinds: ['bin'] }} hint="Doğru gözde olduğunuzu teyit eder." />
            {isSupervisor && (
              <div className="row">
                <button className="btn" onClick={() => setBin(task.locationBarcode)}>
                  <Icon name="check" /> Gözü okutmadan say
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <ScanField key={`product-${props.visits}`} placeholder="Ürün barkodunu okutun (her okutma +1)" onScan={onProduct} suggest={{ kinds: ['product'] }} hint="Koli barkodu koli içi adedi kadar sayar. Listede olmayan ürünü okutursanız eklenir." />
            <div className="banner info-soft">
              <Icon name="eye" /> Sistemdeki adet gösterilmez. Gözde bulunmayan ürüne 0 yazın.
            </div>
            <div>
              {lines.length === 0 && <div className="muted">Sistemde bu gözde ürün görünmüyor. Gözde ürün varsa barkodunu okutun; boşsa sayımı gönderin.</div>}
              {/* a line: the product on top, the count under it, wide enough for a gloved thumb */}
              {lines.map((l) => (
                <div key={l.key} className={`count-line${hit === l.key ? ' hit' : ''}`}>
                  <div className="count-what">
                    <ProductThumb name={l.line.name} imageUrl={l.line.imageUrl} small />
                    <div className="count-name">
                      <b>{l.line.name}</b>
                      <div className="muted mono">
                        {l.line.sku}
                        {!l.found && l.line.lotNo && ` · Lot ${l.line.lotNo}`}
                        {!l.found && l.line.expiryDate && ` · SKT ${fmt.date(l.line.expiryDate)}`}
                      </div>
                      <div className="row" style={{ gap: 6 }}>
                        <SellerChip code={l.line.tenantCode} />
                        {l.found && <span className="chip warn">Listede yoktu</span>}
                      </div>
                    </div>
                    {l.found && (
                      <button className="icon-btn" aria-label="Satırı kaldır" onClick={() => setLines((all) => all.filter((x) => x.key !== l.key))}>
                        <Icon name="trash" size={18} />
                      </button>
                    )}
                  </div>
                  {l.found && (l.line.trackLot || l.line.trackExpiry) && (
                    <div className="count-lot">
                      {l.line.trackLot && (
                        <label className="field">
                          <span>Lot no</span>
                          <input
                            className="input"
                            autoComplete="off"
                            value={l.lotNo}
                            onChange={(e) => setLines((all) => all.map((x) => (x.key === l.key ? { ...x, lotNo: e.target.value } : x)))}
                          />
                        </label>
                      )}
                      {l.line.trackExpiry && (
                        <label className="field">
                          <span>SKT</span>
                          <input
                            className="input"
                            type="date"
                            value={l.expiryDate}
                            onChange={(e) => setLines((all) => all.map((x) => (x.key === l.key ? { ...x, expiryDate: e.target.value } : x)))}
                          />
                        </label>
                      )}
                    </div>
                  )}
                  <div className="count-qty">
                    <button
                      className="count-step"
                      aria-label="Bir azalt"
                      disabled={l.qty === 0}
                      onClick={() => setLines((all) => all.map((x) => (x.key === l.key ? { ...x, qty: Math.max(0, (x.qty ?? 0) - 1) } : x)))}
                    >
                      −
                    </button>
                    <input
                      className="input count-input"
                      inputMode="numeric"
                      aria-label={`${l.line.sku} sayılan adet`}
                      placeholder="?"
                      value={l.qty ?? ''}
                      onChange={(e) => setQty(l.key, e.target.value)}
                    />
                    <button className="count-step" aria-label="Bir artır" onClick={() => bump(l.key, 1)}>
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {/* the send button stays in reach at the bottom while the lines scroll */}
            <div className="count-send">
              <button className="btn primary big" disabled={!ready || busy} onClick={() => void submit()}>
                <Icon name="check" /> Sayımı gönder
              </button>
              {!ready && (
                <span className="hint">
                  {uncounted > 0 ? `${uncounted} satıra adet yazılmadı (yoksa 0).` : 'Bulunan lot takipli ürüne lot ve SKT girin.'}
                </span>
              )}
            </div>
          </>
        )}
      </div>
      {choose && (
        <Modal title="Hangi satıcının ürünü?" onClose={() => setChoose(null)}>
          <div className="choice-list">
            {choose.candidates.map((c) => (
              <button
                key={c.line.productId}
                className="hit-row"
                onClick={() => {
                  addFound(c);
                  setChoose(null);
                }}
              >
                <SellerChip code={c.line.tenantCode} /> <b>{c.line.name}</b> <span className="mono muted">{c.line.sku}</span>
              </button>
            ))}
          </div>
        </Modal>
      )}
    </section>
  );
}

/** The supervisor's queue: counts that came out different, to approve or count again. */
function ReviewQueue({ tasks, onChange }: { tasks: readonly CountTaskSummary[]; onChange: () => void }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const sort = useSort<CountTaskSummary>('count-review', {
    bin: { label: 'Göz', value: (t) => t.locationCode },
    reason: { label: 'Neden', value: (t) => COUNT_REASONS.indexOf(t.reason) },
    differences: { label: 'Farklı satır', value: (t) => t.differences ?? 0, desc: true },
    counted: { label: 'Sayıldı', value: (t) => t.countedAt, desc: true },
  });
  if (tasks.length === 0) {
    return (
      <section className="card">
        <Empty title="Onay bekleyen sayım farkı yok">Sistemle tutmayan sayımlar burada sizin onayınızı bekler.</Empty>
      </section>
    );
  }
  return (
    <>
      {sort.picker}
      <section className="card">
        <div className="table-wrap">
          <table className="t stack-sm count-rows">
            <thead>
              <tr>
                {sort.th('bin')}
                {sort.th('reason')}
                {sort.th('differences', 'num')}
                {sort.th('counted')}
                <th />
              </tr>
            </thead>
            <tbody>
              {sort.apply(tasks).map((t) => (
                <tr key={t.id} className="clickable" onClick={() => setOpenId(t.id)}>
                  <td className="mono" style={{ fontWeight: 700 }}>
                    {t.locationCode}
                  </td>
                  <td>
                    {COUNT_REASON_LABELS[t.reason]}
                    {t.round > 1 && ` · ${t.round}. sayım`}
                  </td>
                  <td className="num" data-label="Farklı satır">
                    <span className="chip warn">{t.differences ?? 0}</span>
                  </td>
                  <td className="nowrap muted">{t.countedAt ? `${fmt.age(t.countedAt)} önce` : '—'}</td>
                  <td className="count-go">
                    <button className="btn sm">İncele</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {openId && <ReviewDialog taskId={openId} onClose={() => setOpenId(null)} onChange={onChange} />}
      </section>
    </>
  );
}

function ReviewDialog({ taskId, onClose, onChange }: { taskId: string; onClose: () => void; onChange: () => void }) {
  const toast = useToast();
  const review = useLoad(() => api<CountReview>('GET', `/api/wms/counts/${taskId}/review`), [taskId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const task = review.data?.task;
  const latest = review.data?.rounds[0];
  const net = latest?.lines.reduce((sum, l) => sum + l.difference, 0) ?? 0;

  const act = async (action: 'approve' | 'recount' | 'cancel') => {
    setBusy(true);
    setError(null);
    try {
      if (action === 'approve') {
        const result = await api<CountApproval>('POST', `/api/wms/counts/${taskId}/approve`);
        toast(
          `${result.locationCode}: stok düzeltildi, göz açıldı${result.requeuedOrders.length > 0 ? `; ${result.requeuedOrders.length} sipariş yeniden ayrıldı` : ''}`,
          'ok',
        );
      } else if (action === 'recount') {
        await api('POST', `/api/wms/counts/${taskId}/recount`, { note: note.trim() || null });
        toast('Göz yeniden sayım listesine alındı', 'ok');
      } else {
        await api('POST', `/api/wms/counts/${taskId}/cancel`, { reason: note.trim() || 'Sorumlu iptal etti' });
        toast('Sayım iptal edildi, göz açıldı', 'ok');
      }
      onChange();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  const pending = task?.status === 'review';
  return (
    <Modal
      title={task ? `${task.locationCode} · ${task.taskNumber}` : 'Sayım'}
      onClose={onClose}
      wide
      footer={
        pending ? (
          <>
            <button className="btn" disabled={busy} onClick={() => void act('cancel')}>
              İptal et
            </button>
            <button className="btn" disabled={busy} onClick={() => void act('recount')}>
              <Icon name="refresh" /> Yeniden saydır
            </button>
            <button className="btn primary" disabled={busy} onClick={() => void act('approve')}>
              <Icon name="check" /> Farkı onayla, stoğu düzelt
            </button>
          </>
        ) : undefined
      }
    >
      {!review.data ? (
        review.error ? (
          <ErrorBanner message={review.error} />
        ) : (
          <Spinner />
        )
      ) : (
        <div className="stack">
          {error && <div className="banner bad">{error}</div>}
          <div className="row">
            <span className="chip">{COUNT_REASON_LABELS[review.data.task.reason]}</span>
            {review.data.task.resolution && <span className="chip ok">{COUNT_RESOLUTION_LABELS[review.data.task.resolution]}</span>}
            {review.data.task.note && <span className="muted">{review.data.task.note}</span>}
          </div>
          {pending && latest && (
            <div className={`banner ${net < 0 ? 'warn' : 'info'}`}>
              Onaylarsanız gözdeki stok {net < 0 ? `${-net} adet azalır (fire)` : net > 0 ? `${net} adet artar` : 'lot bazında düzeltilir'}; göz açılır.
              {net < 0 && ' Bu gözden ayrılmış ama artık sığmayan siparişler başka gözlerden yeniden ayrılır.'}
            </div>
          )}
          {review.data.rounds.map((round) => (
            <div key={round.round}>
              <h3 style={{ margin: '4px 0 8px' }}>
                {round.round}. sayım
                <span className="muted" style={{ fontWeight: 500, fontSize: 13 }}>
                  {round.countedBy ? ` · ${round.countedBy}` : ''}
                  {round.countedAt ? ` · ${fmt.dateTime(round.countedAt)}` : ''}
                </span>
              </h3>
              {/* a phone: each product a block, its system / counted / difference named under it */}
              <div className="table-wrap">
                <table className="t stack-sm count-diff">
                  <thead>
                    <tr>
                      <th>Ürün</th>
                      <th>Lot / SKT</th>
                      <th className="num">Sistem</th>
                      <th className="num">Sayılan</th>
                      <th className="num">Fark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {round.lines.map((l, i) => {
                      const lot = [l.lotNo, l.expiryDate ? fmt.date(l.expiryDate) : null].filter(Boolean).join(' · ');
                      return (
                        <tr key={i} className={l.difference === 0 ? 'muted-row' : ''}>
                          <td className="count-diff-what">
                            <SellerChip code={l.tenantCode} /> <b>{l.name}</b> <span className="mono muted">{l.sku}</span>
                          </td>
                          <td className={`mono${lot ? '' : ' count-diff-none'}`} data-label="Lot / SKT">
                            {lot || '—'}
                          </td>
                          <td className="num" data-label="Sistem">
                            {l.expected}
                          </td>
                          <td className="num" data-label="Sayılan">
                            {l.counted}
                          </td>
                          <td className="num count-diff-n" data-label="Fark">
                            {l.difference === 0 ? '—' : <span className={`chip ${l.difference < 0 ? 'bad' : 'info'}`}>{l.difference > 0 ? `+${l.difference}` : l.difference}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {pending && (
            <div className="field">
              <label htmlFor="count-note">Not (yeniden sayım veya iptal için)</label>
              <input id="count-note" className="input count-text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Örn. raf arkası ve üst göz de kontrol edilsin" />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function History({ warehouseId, canReview }: { warehouseId: string | null; canReview: boolean }) {
  const list = useLoad(
    () => (warehouseId ? api<CountTaskSummary[]>('GET', `/api/wms/counts?warehouseId=${warehouseId}&status=closed,cancelled`) : Promise.resolve([])),
    [warehouseId],
  );
  const [openId, setOpenId] = useState<string | null>(null);
  const sort = useSort<CountTaskSummary>('count-history', {
    bin: { label: 'Göz', value: (t) => t.locationCode },
    task: { label: 'Görev', value: (t) => t.taskNumber },
    reason: { label: 'Neden', value: (t) => COUNT_REASONS.indexOf(t.reason) },
    result: { label: 'Sonuç', value: (t) => (t.resolution ? COUNT_RESOLUTION_LABELS[t.resolution] : null) },
    closed: { label: 'Kapandı', value: (t) => t.closedAt, desc: true },
  });
  if (list.error) return <ErrorBanner message={list.error} onRetry={() => void list.reload()} />;
  if (!list.data) return <Spinner />;
  return (
    <>
      {sort.picker}
      <section className="card">
        {list.data.length === 0 ? (
          <Empty title="Henüz kapanan sayım yok" />
        ) : (
          <div className="table-wrap">
            <table className="t stack-sm count-rows">
              <thead>
                <tr>
                  {sort.th('bin')}
                  {sort.th('task')}
                  {sort.th('reason')}
                  {sort.th('result')}
                  {sort.th('closed')}
                </tr>
              </thead>
              <tbody>
                {sort.apply(list.data).map((t) => (
                  <tr key={t.id} className={canReview ? 'clickable' : ''} onClick={() => canReview && setOpenId(t.id)}>
                    <td className="mono" style={{ fontWeight: 700 }}>
                      {t.locationCode}
                    </td>
                    <td className="mono muted">{t.taskNumber}</td>
                    <td>{COUNT_REASON_LABELS[t.reason]}</td>
                    <td>
                      {t.resolution && (
                        <span className={`chip ${t.resolution === 'matched' ? 'ok' : t.resolution === 'adjusted' ? 'warn' : ''}`}>{COUNT_RESOLUTION_LABELS[t.resolution]}</span>
                      )}
                    </td>
                    <td className="nowrap">{t.closedAt ? fmt.dateTime(t.closedAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {openId && <ReviewDialog taskId={openId} onClose={() => setOpenId(null)} onChange={() => void list.reload()} />}
      </section>
    </>
  );
}

/** Periyodik sayım: a block of an aisle, or bins by code. */
function OrderDialog({ warehouseId, onClose, onDone }: { warehouseId: string; onClose: () => void; onDone: (result: CountOrderResult) => void }) {
  const [mode, setMode] = useState<'aisle' | 'codes'>('aisle');
  const [aisle, setAisle] = useState('');
  const [rackFrom, setRackFrom] = useState('1');
  const [rackTo, setRackTo] = useState('99');
  const [codes, setCodes] = useState('');
  const [onlyWithStock, setOnlyWithStock] = useState(true);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const list = codes.split(/[\s,;]+/).map((c) => c.trim()).filter(Boolean);
  const valid = mode === 'aisle' ? /^[A-Za-z]{1,3}$/.test(aisle.trim()) && Number(rackFrom) >= 1 && Number(rackTo) >= Number(rackFrom) : list.length > 0;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body =
        mode === 'aisle'
          ? { warehouseId, aisle: aisle.trim().toUpperCase(), rackFrom: Number(rackFrom), rackTo: Math.min(99, Number(rackTo)), onlyWithStock, note: note.trim() || null }
          : { warehouseId, codes: list, onlyWithStock, note: note.trim() || null };
      onDone(await api<CountOrderResult>('POST', '/api/wms/counts', body));
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Sayım emri ver"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={!valid || busy} onClick={() => void submit()}>
            Sayıma al
          </button>
        </>
      }
    >
      <div className="stack">
        {error && <div className="banner bad">{error}</div>}
        <div className="tabs" role="tablist">
          <button className={mode === 'aisle' ? 'on' : ''} onClick={() => setMode('aisle')}>
            Koridor
          </button>
          <button className={mode === 'codes' ? 'on' : ''} onClick={() => setMode('codes')}>
            Göz kodları
          </button>
        </div>
        {mode === 'aisle' ? (
          <div className="form-grid">
            <div className="field">
              <label htmlFor="c-aisle">Koridor</label>
              <input id="c-aisle" className="input count-text" value={aisle} maxLength={3} onChange={(e) => setAisle(e.target.value)} placeholder="A" />
            </div>
            <div className="field">
              <label htmlFor="c-from">Raf (ilk)</label>
              <input id="c-from" className="input count-text" inputMode="numeric" value={rackFrom} onChange={(e) => setRackFrom(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="c-to">Raf (son)</label>
              <input id="c-to" className="input count-text" inputMode="numeric" value={rackTo} onChange={(e) => setRackTo(e.target.value)} />
            </div>
          </div>
        ) : (
          <div className="field">
            <label htmlFor="c-codes">Göz kodları</label>
            <textarea id="c-codes" className="input count-text" rows={4} value={codes} onChange={(e) => setCodes(e.target.value)} placeholder="A-01-2-1, A-01-2-2 veya etiket barkodları" />
            <span className="hint">{list.length} göz</span>
          </div>
        )}
        <label className="row" style={{ gap: 8 }}>
          <input type="checkbox" checked={onlyWithStock} onChange={(e) => setOnlyWithStock(e.target.checked)} /> Yalnızca stoklu gözler
        </label>
        <div className="field">
          <label htmlFor="c-note">Not</label>
          <input id="c-note" className="input count-text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Örn. ay sonu sayımı" />
        </div>
        <p className="hint" style={{ margin: 0 }}>
          Sayıma alınan gözler sayılana kadar kilitlenir: yeni siparişe ayrılmaz ve içine yerleştirme yapılmaz.
        </p>
      </div>
    </Modal>
  );
}
